import { BlurEvent, ChangeEvent, Checkbox, Component, ComponentPropDefinitionUtil, DataSourceMode, Panel, Textbox, TextboxProps } from "@mcleod/components";
import { GeneralSettings, ModelRow, ObjectUtil, StringUtil, UserSettings } from "@mcleod/core";
import { PanelOpenLayout } from "@mcleod/designer";
import { RowWebMenuItemFields } from "../models/autogen/AutogenModelWebMenuItem";
import { AutogenLayoutMenuItemEditor } from "./autogen/AutogenLayoutMenuItemEditor";
import { DatabaseMenuItem } from "./DatabaseMenuItem";
import { MenuItemDefaults } from "./MenuItemDefaults";

export interface EditMenuItemResult {
    menuItem: DatabaseMenuItem;
    hasChanged: boolean;
}

export class MenuItemEditor extends AutogenLayoutMenuItemEditor {
    private menuItem: DatabaseMenuItem;
    private _defaultValues: Partial<RowWebMenuItemFields>;
    private _defaultPath: string;

    private defaultLabelColors: Record<string, string> = {};
    private loadingMenuItem: boolean = false;
    private originalMenuItemData: Partial<RowWebMenuItemFields>;

    override onLoad() {
        this.configureForCurrentUser();
        // gross but avoids having to export Company
        this.textboxCompanyType.items = ComponentPropDefinitionUtil.getComponentPropertyDefinitions().companyType.dropdownProps.items;
        this.textboxPath._inputDiv.appendChild(this.buttonLayoutSelector._element);

        const blurListener = (event: BlurEvent) => {
            if (event.domEvent.relatedTarget === this.buttonLayoutSelector._element) {
                return;
            }
            const textbox = event.target as Textbox;
            if (event.changedWhileFocused) {
                this.syncCaptionForDefaultValue(textbox);
            }
            if (textbox !== this.textboxPath && textbox.isEmpty() && this.defaultValues[textbox.field] != null) {
                textbox.text = this.defaultValues[textbox.field];
            }
        }

        const changeListener = (event: ChangeEvent) => {
            if (event.userInitiatedChange) {
                this.syncCaptionForDefaultValue(event.target as Component);
            }
        }

        for (const comp of this.boundCaptionedComponents) {
            if (comp instanceof Textbox && comp != this.textboxId) {
                comp.addBlurListener(blurListener);
                this.defaultLabelColors[comp.id] = comp["_captionLabel"].color ?? "default";
            } else if (comp instanceof Checkbox) {
                comp.addChangeListener(changeListener);
                this.defaultLabelColors[comp.id] = comp["_captionLabel"].color ?? "default";
            }
        }

        this.textboxPath.addBlurListener((event: BlurEvent) => {
            if (this.textboxPath.isEmpty()) {
                this.defaultValues = null;
            } else if (event.changedWhileFocused) {
                this.populateDefaultValues();
            }
        });
    }

    private configureForCurrentUser() {
        const runningInIDE = GeneralSettings.getSingleton().isRunningInIDE();
        const isSupportUser = UserSettings.get().is_support_user;

        const removeComponent = (component: Component) => {
            component.setProps({ visible: false, enabled: false, dataSource: null });
            component.parent?.remove(component);
        }

        if (!runningInIDE && !isSupportUser) {
            removeComponent(this.checkboxIdeOnly);
            removeComponent(this.checkboxLocalOnly);
            removeComponent(this.checkboxMcleodOnly);
            this.textboxClassName.enabled = false;
        }
    }


    private get boundCaptionedComponents(): (Textbox | Checkbox)[] {
        return this.mainDataSource.boundComponents.filter(comp => comp instanceof Textbox || comp instanceof Checkbox);
    }

    public async configureForAddMode() {
        const menuItem = await DatabaseMenuItem.createNewMenuItem();
        this.setMenuItemAndMode(menuItem, DataSourceMode.ADD);
    }

    public configureForUpdateMode(menuItem: DatabaseMenuItem) {
        this.originalMenuItemData = menuItem.itemData.createBasicCopy().data;
        this.setMenuItemAndMode(menuItem.createCopy(), DataSourceMode.UPDATE);
    }

    public setMenuItemAndMode(menuItem: DatabaseMenuItem, mode: DataSourceMode) {
        this.mainDataSource.setRowsAndMode(mode, [menuItem.itemData]);
        this.menuItem = menuItem;
        this.syncMenuItem();
    }

    private syncMenuItem() {
        if (this.menuItem.isLeaf && this.mainDataSource.mode === DataSourceMode.UPDATE) {
            this.loadingMenuItem = true;
            this.populateDefaultValues().finally(() => this.loadingMenuItem = false);
            return;
        }
        if (!this.menuItem.isLeaf) {
            const props: Partial<TextboxProps> = {
                dataSource: null,
                required: false,
                visible: false,
            }
            this.textboxPath.setProps(props);
            this.textboxFavoriteCaption.setProps(props);
            this.panelPath.visible = false;
        }
        this.populateDefaultValues();
    }

    async populateDefaultValues() {
        const path = this.activeRow.get("path");
        if (StringUtil.isEmptyString(path)) {
            this.defaultValues = MenuItemDefaults.getStandardDefaults();
            this.syncCaptionsForDefaultValues();
            return;
        }

        if (this._defaultPath === path) {
            return;
        }

        this.defaultValues = await this.getDefaultDataPromise(path);
        this.syncCaptionsForDefaultValues();
    }

    private set defaultValues(defaults: Partial<RowWebMenuItemFields>) {
        const cleared = this.clearDefaultValues();
        const path = defaults?.path;
        defaults ??= MenuItemDefaults.getStandardDefaults();
        delete defaults?.path;
        ObjectUtil.deleteNullOrEmptyProps(defaults);

        const pathChanged = path != this._defaultPath;

        this._defaultPath = path;
        this._defaultValues = defaults;

        for (const [field, value] of Object.entries(this._defaultValues)) {
            if (this.shouldSetDefaultValue(field, pathChanged, cleared)) {
                this.activeRow.set(field, value);
            }
        }
    }

    private get defaultValues(): Partial<RowWebMenuItemFields> {
        return this._defaultValues ?? MenuItemDefaults.getStandardDefaults();
    }

    private shouldSetDefaultValue(field: string, pathChanged: boolean, cleared: any): boolean {
        const needsDefault = pathChanged || this.activeRow.isNull(field) || cleared[field] != null;
        const canSet = !this.loadingMenuItem || this.activeRow.isNull(field);
        return needsDefault && canSet;
    }

    private clearDefaultValues(row: ModelRow = this.activeRow): Partial<RowWebMenuItemFields> {
        const cleared: Partial<RowWebMenuItemFields> = {};
        if (this.loadingMenuItem)
            return cleared;
        for (const [field, value] of Object.entries(this.defaultValues)) {
            if (this.isDefaultValue(field, row.get(field))) {
                row.set(field, undefined);
                row.resetOriginalData(field);
                cleared[field] = value;
            }
        }
        return cleared;
    }

    private buttonLayoutSelectorOnClick() {
        const currentSelection = this.textboxPath.text;
        const selector = new PanelOpenLayout(currentSelection);
        selector.showAsDialog().finally(() => {
            if (selector.wasCancelled)
                return;
            const selectedLayout = selector.getResultValue();
            if (selectedLayout != null && selectedLayout != currentSelection) {
                this.activeRow.set("path", selectedLayout);
                this.populateDefaultValues();
            }
        });
    }

    private syncCaptionsForDefaultValues() {
        for (const comp of this.boundCaptionedComponents) {
            this.syncCaptionForDefaultValue(comp);
        }
    }

    private syncCaptionForDefaultValue(component: Component) {
        if (ObjectUtil.isEmptyObject(this.defaultValues))
            return;

        const labelCaption = component["_captionLabel"];
        if (labelCaption == null)
            return;

        const componentValue = this.getComponentValue(component);

        const isDefault = this.isDefaultValue(component.field, componentValue);
        if (!isDefault) {
            labelCaption.color = "primary";
        } else {
            labelCaption.color = this.defaultLabelColors[component.id] ?? "default";
        }
    }

    private getComponentValue(component: Component) {
        let componentValue = component.getDataValue();
        if (component instanceof Checkbox) {
            componentValue = component.checked ? component.valueChecked : component.valueUnchecked;
        }
        if (StringUtil.isEmptyString(componentValue))
            componentValue = undefined;
        return componentValue;
    }

    private isDefaultValue(field: string, value: any) {
        return this.defaultValues != null && this.defaultValues[field] === value;
    }

    private getDefaultDataPromise(path: string): Promise<Partial<RowWebMenuItemFields>> {
        this._element.style.pointerEvents = "none";
        return MenuItemDefaults.get(path).finally(() => this._element.style.pointerEvents = "");
    }

    /**
     * Returns a copy of the original menu item and a boolean indicating if the web_menu_item has changed
     * Any default values are removed from the web_menu_item data and not considered when determining if the data has changed.
     */
    public getEditMenuItemResult(): EditMenuItemResult {
        const boundDataRow = this.mainDataSource.getDataboundValues();

        // Create a copy to prevent FieldUpdateListeners added by this.mainDataSource from interfering with our changes
        const menuItemResult = this.menuItem.createCopy();
        menuItemResult.caption = boundDataRow.get("caption");
        ObjectUtil.deleteNullOrEmptyProps(boundDataRow.data);
        const hasChanged = this.menuItemChanged(boundDataRow);
        if (hasChanged) {
            menuItemResult.itemData.clear();
            menuItemResult.itemData.setValues(boundDataRow.data);
        }

        return {
            menuItem: menuItemResult,
            hasChanged: hasChanged
        }
    }

    private menuItemChanged(boundDataRow: ModelRow): boolean {
        const appending = this.mainDataSource.mode === DataSourceMode.ADD
        const originalRow = new ModelRow(this.mainDataSource.url, appending, {...this.originalMenuItemData as any});
        originalRow.resetOriginalData();

        originalRow.setValues(boundDataRow.data);
        for (const field of Object.keys(this.defaultValues)) {
            const currentValue = boundDataRow.data[field];
            if (this.isDefaultValue(field, currentValue)) {
                const originalValue = originalRow.originalData[field];
                originalRow.set(field, null);
                if (originalValue != null && originalValue != currentValue) {
                    // If the original value is not the default, set it to null to in order to nullify the field in the database.
                    boundDataRow.set(field, null);
                } else {
                    delete boundDataRow.data[field];
                }
            }
        }
        return originalRow.hasChanged();
    }

    createReferencesPanel() {
        return Panel.createNoMarginNoPaddingPanel({fillHeight: true, fillRow: true});
    }
}
